import { api, apiPublic } from "api/api";
import { AxiosError } from "axios";
import Pessoa from "model/administracao/cadastro/Pessoa";
import Setor from "model/administracao/cadastro/Setor";
import Usuario from "model/administracao/cadastro/Usuario";
import ClassificacaoRequisito from "model/auditoria5s/ClassificacaoRequisito";
import Auditoria from "model/auditoria5s/auditoria/Auditoria";
import GerarAuditoriaDto from "model/auditoria5s/gerar/GerarAuditoriaDto";
import PlanoAuditoria from "model/auditoria5s/planoDeAuditoria/PlanoAuditoria";
import SetorPlanoAuditoria from "model/auditoria5s/planoDeAuditoria/SetorPlanoAuditoria";
import { ChartBarModel } from "model/chart/ChartBarModel";
import UnidadeOrganizacional from "model/indicadores/UnidadeOrganizacional";
import Page from "model/page/Page";
import Pageable from "model/page/Pageable";
import { DataTableFilterMeta } from "primereact/datatable";
import { BaseServiceCadastros } from "service/BaseServiceCadastros";
import { getFilters } from "utils/filterUtils";

class AuditoriaService implements BaseServiceCadastros<Auditoria> {

    pesquisar(page: Pageable, filter?: DataTableFilterMeta | null) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/auditoria/pesquisar`;
            if (filter) {
                url += getFilters(filter)
            }
            const params = {
                page: page.page,
                size: page.size,
                sort: "numero,desc",
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    pesquisarMinhasAuditorias(page: Pageable, filter?: DataTableFilterMeta | null) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/auditoria/pesquisar/minhas`;
            if (filter) {
                url += getFilters(filter)
            }
            const params = {
                page: page.page,
                size: page.size,
                sort: "numero,desc",
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    consultar(id: number) {
        return new Promise<Auditoria>((resolve, reject): void => {
            api
                .get(`/auditoria/consultar/${id}`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    consultarPublic(idCriptografado: string) {
        return new Promise<Auditoria>((resolve, reject): void => {
            apiPublic
                .get(`/public/auditoria/consultar?idCriptografado=${encodeURIComponent(idCriptografado)}`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    consultarRequisitos() {
        return new Promise<ClassificacaoRequisito[]>((resolve, reject): void => {
            api
                .get(`/auditoria/requisitos`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    incluir(auditoria: Auditoria) {
        return new Promise<Auditoria>((resolve, reject): void => {
            api
                .post(`/auditoria/incluir`, JSON.stringify(auditoria))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    editar(auditoria: Auditoria) {
        return new Promise<Auditoria>((resolve, reject): void => {
            api
                .put(`/auditoria/editar`, JSON.stringify(auditoria))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    excluir(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .delete(`/auditoria/excluir/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    gerarNovaAuditoria(dados: GerarAuditoriaDto) {
        return new Promise<Auditoria>((resolve, reject): void => {
            let url = `/auditoria/gerarNovaAuditoria`;
            api
                .post(url, dados)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    cancelarAuditoria(auditoria: Auditoria) {
        return new Promise<Auditoria>((resolve, reject): void => {
            let url = `/auditoria/cancelar`;
            api
                .post(url, auditoria)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    sortearSetores(quantidadeSetores: number, setoresSelecionados: Setor[]) {
        return new Promise<Setor[]>((resolve, reject): void => {
            api
                .get(`/auditoria/sortearSetores?quantidadeSetores=${quantidadeSetores}&idsSetoresElegiveis=${setoresSelecionados.map(setor => setor.id).join(',')}`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getSetoresDisponiveis(unidadeOrganizacionalId?: number, equalsUnidadeOrganizacional: boolean = true) {
        let url = `/auditoria/setoresDisponiveis`;
        if(unidadeOrganizacionalId) {
            url += `?idUnidade=${unidadeOrganizacionalId}&equalsUnidade=${equalsUnidadeOrganizacional}`;
            
        }
        return new Promise<Setor[]>((resolve, reject): void => {
            api
                .get(url)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }
    getPlanosDisponiveis(setorId?: number, equalsSetor: boolean = true) {
        let url = `/auditoria/planosDisponiveis`;
        if(setorId) {
            url += `?idSetor=${setorId}&equalsSetor=${equalsSetor}`;
        }
        return new Promise<PlanoAuditoria[]>((resolve, reject): void => {
            api
                .get(url)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getUsuariosDisponiveis() {
        return new Promise<Usuario[]>((resolve, reject): void => {
            api
                .get(`/auditoria/usuariosDisponiveis`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getUsuarios() {
        return new Promise<Array<Usuario>>((resolve, reject): void => {
            api
                .get(`/auditoria/usuarios`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    unidadesOrgan() {
        return new Promise<Array<UnidadeOrganizacional>>((resolve, reject): void => {
            let url = `/auditoria/unidadesOrganizacionais`;
            api
                .get(url)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getStatusAuditoriaFilter() {
        return [
            { label: 'Criada', value: 'C' },
            { label: 'Em andamento', value: 'A' },
            { label: 'Finalizada', value: 'F' },
            { label: 'Cancelada', value: 'X' }
        ]
    }

    getStatusAuditoria(status: string): string {

        if (status === 'EM_ANDAMENTO') {
            return 'Em andamento';
        } else if (status === 'FINALIZADA') {
            return 'Finalizada';
        } else if (status === 'CANCELADA') {
            return 'Cancelada';
        }

        return 'Criada';
    }

    getStatusItemAuditoria(status: string): string {

        if (status === 'EM_ANDAMENTO') {
            return 'Em andamento';
        } else if (status === 'FINALIZADO') {
            return 'Finalizado';
        }

        return 'Criado';
    }

    getStatusAcaoAuditoria(status: string): string {

        if (status === 'EM_ANDAMENTO') {
            return 'Em andamento';
        } else if (status === 'FINALIZADO') {
            return 'Finalizado';
        }

        return 'Criado';
    }

    getResultadoItemAuditoria(status: string): string {

        if (status === 'CONFORME') {
            return 'Conforme';
        } else if (status === 'PARCIAL') {
            return 'Parcial';
        } else if (status === 'NAO_CONFORME') {
            return 'Não Conforme';
        } else if (status === 'NAO_APLICAVEL') {
            return "Não aplicável";
        }
        return "";
    }

    getGraficoNotaPorSetor() {

        return new Promise<ChartBarModel>((resolve, reject): void => {
            api
                .get(`/auditoria/app/grafico/notaPorSetor`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });

    }

    getGraficoEvolucaoSetor() {

        return new Promise<ChartBarModel>((resolve, reject): void => {
            api
                .get(`/auditoria/app/grafico/evolucaoSetor`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });

    }

    getRelatorio(id: number) {

        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/report/${id}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioPublic(idCriptografado: string) {
        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/public/auditoria/report/simplificado/imagens?idCriptografado=${encodeURIComponent(idCriptografado)}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    testarReport(nomeAcao: string) {
        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/report/teste/${nomeAcao}`, { responseType: 'arraybuffer' })
                .then((response) => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioSimplificado(id: number) {

        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/report/simplificado/${id}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioSimplificadoComImagens(id: number) {

        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/report/simplificado/imagens/${id}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioResultado(id: number) {

        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/report/resultado/${id}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioGeral() {

        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/auditoria/reportGeral`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioListagem(filter?: DataTableFilterMeta | null) {
        return new Promise<string>((resolve, reject): void => {
            let url = `/auditoria/report/listagem`;
            if (filter) {
                url += getFilters(filter)
            }
            api
                .get(url, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }


}

export default AuditoriaService;
